<template>
  <div>
    <Card cardClasses="shadow-sm border-0">
      <!-- Header Section -->
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h4 class="m-0 text-primary">
          <i class="bi bi-cart-plus me-2"></i>Direct Purchases Management
        </h4>
        <div class="d-flex">
          <button class="btn btn-outline-primary me-2" @click="goToProducts">
            <i class="bi bi-box-seam me-1"></i> View Products
          </button>
          <button
            v-if="profile.role !== 'user'"
            class="btn btn-primary"
            @click="purchaseModal = true;">
            <i class="bi bi-plus-circle me-1"></i> Add Direct Purchase
          </button>
        </div>
      </div>

      <div class="container-fluid p-0">
        <!-- Loading Spinner -->
        <div v-if="loading" class="text-center py-5">
          <Spinner spinner-class="mb-1" />
          <p class="text-muted mt-2">Loading direct purchases...</p>
        </div>

        <!-- Purchases Content -->
        <div v-if="!loading" class="purchases-content fade-in">
          <!-- Purchases Count & Search -->
          <div class="d-flex flex-wrap align-items-center justify-content-between mb-3">
            <div class="d-flex align-items-center mb-2 mb-sm-0">
              <span class="badge bg-primary rounded-pill me-2">{{ paginationData.total || purchaseData.length }}</span>
              <span class="text-muted">Direct Purchases</span>
            </div>
            <UInput v-model="dataTableSearch" size="md" placeholder="Search purchase" class="mb-0 w-auto" />
          </div>

          <!-- Purchases Table -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="table-responsive">
              <table class="table table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>CODE</th>
                    <th>SUPP. INVOICE</th>
                    <th>SUPPLIER</th>
                    <th>BY USER</th>
                    <th>STATUS</th>
                    <th>DATE</th>
                    <th class="text-end">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in paginatedPurchases" :key="row[0]" class="purchase-row" :class="{'bg-light': index % 2 === 0}">
                    <td>{{ row[0].split("-").pop() }}</td>
                    <td>{{ row[1] }}</td>
                    <td>{{ row[2] }}</td>
                    <td>{{ row[3] }}</td>
                    <td>{{ row[4] }}</td>
                    <td>
                      <Badge pill :type="getPillByStatus(row[5])">
                        {{ row[5] }}
                      </Badge>
                    </td>
                    <td>{{ row[6] }}</td>
                    <td class="text-end">
                      <button class="btn btn-sm btn-outline-primary" @click="purchaseDetails(row[0])">
                        <i class="bi bi-eye"></i> Details
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Pagination Controls -->
          <div class="d-flex flex-wrap pt-3 pb-4 align-items-end mx-4 justify-content-between">
            <div class="me-sm-4 mb-sm-0 mb-3">
              <p class="mb-2 small">Rows per page</p>
              <select class="form-select" v-model.number="dataTablePerPage" data="">
                <option v-for="item in [5,10,25,50,100]" :key="item" :value="item">{{item}}</option>
              </select>
            </div>
            <div>
              <Pagination
                v-model="dataTablePage"
                :pages="totalPages"
                :threshold="2">
                <template #previous>Prev</template>
                <template #next>Next</template>
              </Pagination>
            </div>
          </div>

          <!-- Back Button -->
          <div class="d-flex justify-content-center">
            <button class="btn btn-outline-secondary" @click="$router.go(-1)">
              <i class="bi bi-arrow-left me-1"></i> Back
            </button>
          </div>
        </div>
      </div>
    </Card>

    <!-- Add Purchase Modal -->
    <Modal :open="purchaseModal" @closed="purchaseModal = false" dialog-size="modal-lg">
      <div class="modal-header bg-light">
        <h5 class="modal-title m-0 d-flex align-items-center">
          <i class="bi bi-cart-plus me-2 text-primary"></i>Add Direct Purchase
        </h5>
        <button @click="purchaseModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body p-4">
        <form @submit.prevent="">
          <!-- Purchase Information Section -->
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-header bg-light py-2">
              <h6 class="mb-0"><i class="bi bi-info-circle me-2"></i>Purchase Information</h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label for="code" class="form-label fw-bold">Purchase Code:</label>
                    <div class="input-group">
                      <span class="input-group-text"><i class="bi bi-upc"></i></span>
                      <input type="text" class="form-control" placeholder="Code" v-model="purchase.code" readonly>
                    </div>
                    <small class="form-text text-muted">Automatically generated code</small>
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="form-group mb-3">
                    <label for="supplier" class="form-label fw-bold">Supplier:</label>
                    <select class="form-select" v-model="purchase.supplier_id" data="">
                      <option value="" disabled selected>Select Supplier</option>
                      <option v-for="supplier in suppliers" :value="supplier.id" :key="supplier.id">
                        {{supplier.name}}
                      </option>
                    </select>
                    <small class="form-text text-muted">Select the supplier for this purchase</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Additional Details Section -->
          <div class="card border-0 shadow-sm mb-3">
            <div class="card-header bg-light py-2">
              <h6 class="mb-0"><i class="bi bi-card-text me-2"></i>Additional Details</h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-12">
                  <div class="form-group mb-3">
                    <label for="description" class="form-label fw-bold">Description:</label>
                    <textarea class="form-control" placeholder="Enter purchase description or notes" v-model="purchase.description" rows="3"></textarea>
                    <small class="form-text text-muted">Add any relevant notes about this purchase</small>
                  </div>
                </div>
              </div>

              <!-- Info Alert -->
              <div class="alert alert-info d-flex align-items-center mt-3">
                <i class="bi bi-info-circle-fill me-2"></i>
                <div>
                  After creating this purchase, you'll be able to add products and supplier invoice details.
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer bg-light">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="purchaseModal = false">
          <i class="bi bi-x-circle me-1"></i> Cancel
        </button>
        <button type="button" class="btn btn-primary" @click="createPurchase">
          <i class="bi bi-check-circle me-1"></i> Create Purchase
        </button>
      </div>
    </Modal>
    <!-- Add Purchase Modal End -->
  </div>
</template>

<script>
import directPurchaseService from "@/api/directPurchaseService";
import router from "@/router";
import {decrypt} from "@/utils/crypto";
import confirmDialog from "@/utils/confirmDialog";
import formatDate from "@/utils/formatDate";
import supplierService from "@/api/supplierService";

export default {
  name: "DirectPurchases",

  data(){
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    return {
      organization: {},
      store: {},
      suppliers: [],
      profile: {},
      loading: true,
      purchaseModal: false,
      purchase: {
        organization_id: this.organization.id,
        store_id: this.store.id,
        user_id: this.profile.id,
      },
      dataTableHeaders: [
        '#',
        'Code',
        'Supp. Invoice',
        'Supplier',
        'By User',
        'Status',
        'Date',
        null,
        null
      ],
      dataTable: [],
      paginationData: {},
      dataTableSearch: '',
      dataTablePage: 1,
      dataTablePerPage: 10,
      subscription: {
        package: {}
      },
      searchTimeout: null,
    }
  },

  methods:{
    confirmDialog,
    getPillByStatus(cellData) {
      switch (cellData) {
        case 'pending':
          return 'warning';
        case 'complete':
          return 'success';
        case 'submitted':
          return 'primary';
      }
    },
    async createPurchase() {
      let response = await directPurchaseService.createPurchase(this.purchase)
      if (response) {
        this.purchaseModal = false
        await this.getPurchases()
      }
    },
    async getPurchases() {
      const params = {
        page: this.dataTablePage,
        per_page: this.dataTablePerPage
      }
      
      if (this.dataTableSearch) {
        params.search = this.dataTableSearch
      }
      
      const response = await directPurchaseService.getPurchases(this.store.id, params)
      // Handle paginated response structure
      if (response && response.data) {
        this.dataTable = response.data
        this.paginationData = {
          current_page: response.current_page,
          last_page: response.last_page,
          per_page: response.per_page,
          total: response.total
        }
      } else {
        // Fallback for non-paginated response (backward compatibility)
        this.dataTable = Array.isArray(response) ? response : []
        this.paginationData = {}
      }
      
      let nextNo = await directPurchaseService.getPurchaseNextNo(this.store.id)
      this.purchase.code = nextNo.next
      this.loading = false
    },
    purchaseDetails(id) {
      router.push('/direct-purchase/' + id)
    },
    goToProducts() {
      router.push('/products')
    },
  },

  computed: {
    purchaseData(){
      let data = [];
      for (let i = 0; i < this.dataTable.length; i++) {
        let fields = [
          this.dataTable[i].id,
          this.dataTable[i].code,
          this.dataTable[i].invoice,
          this.dataTable[i].supplier.code + ' - ' + this.dataTable[i].supplier.name,
          this.dataTable[i].user.first_name + ' ' + this.dataTable[i].user.last_name,
          this.dataTable[i].status,
          formatDate(this.dataTable[i].created_at),
        ];
        data.push(fields);
      }
      return data;
    },
    filteredPurchases(){
      // With server-side pagination, filtering is done by the API
      // Return the data as-is for server-side, or filter client-side as fallback
      if (this.paginationData.total !== undefined) {
        return this.purchaseData;
      }
      
      // Fallback to client-side filtering for backward compatibility
      if (!this.dataTableSearch) return this.purchaseData;
      const term = this.dataTableSearch.toLowerCase();
      return this.purchaseData.filter(p =>
          (p[1] && p[1].toLowerCase().includes(term)) ||
          (p[2] && p[2].toLowerCase().includes(term)) ||
          (p[3] && p[3].toLowerCase().includes(term)) ||
          (p[4] && p[4].toLowerCase().includes(term))
      );
    },
    paginatedPurchases(){
      // With server-side pagination, return data as-is
      if (this.paginationData.total !== undefined) {
        return this.filteredPurchases
      }
      
      // Fallback to client-side pagination for backward compatibility
      const start = (this.dataTablePage - 1) * this.dataTablePerPage;
      return this.filteredPurchases.slice(start, start + this.dataTablePerPage);
    },
    totalPages(){
      // If we have server-side pagination data, use it
      if (this.paginationData.last_page !== undefined) {
        return this.paginationData.last_page
      }
      
      // Fallback to client-side pagination for backward compatibility
      return Math.ceil(this.filteredPurchases.length / this.dataTablePerPage) || 1;
    }
  },

  watch: {
    dataTablePerPage(){
      this.dataTablePage = 1;
      this.getPurchases();
    },
    dataTablePage() {
      this.getPurchases();
    },
    dataTableSearch: {
      handler() {
        this.dataTablePage = 1;
        // Debounce search to avoid too many API calls
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
          this.getPurchases();
        }, 500);
      }
    }
  },

  created: async function () {
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    await this.getPurchases()
    const suppliersData = await supplierService.getSuppliers({ per_page: 1000 })
    this.suppliers = Array.isArray(suppliersData?.data) ? suppliersData.data.filter(s => s != null) : []
    this.subscription = JSON.parse(decrypt(sessionStorage.getItem('subscription')))
  },

}
</script>

<style scoped>
/* Purchases page styles */
.purchases-content {
  padding: 0.5rem;
}

/* Table styles */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 1rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
}

.purchase-row {
  transition: all 0.2s ease;
}

.purchase-row:hover {
  background-color: #f8f9fa;
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

/* Badge styles */
.badge {
  font-weight: 500;
  padding: 0.35em 0.65em;
  font-size: 0.75em;
  border-radius: 6px;
}

/* Animation for content loading */
.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
